import { GAME } from "@/config";
import { isArtLoaded } from "@/sprites/artManifest";
import type { GameState } from "@/state/GameState";

/**
 * The dollhouse slot tables (v0.9 RFC §4): per scene, WHAT renders WHERE at
 * WHICH progression state. Data, not code — the letters.ts pattern. The
 * storyboards are the sole source of the prop inventory (ruling 10); slot
 * geometry is extracted from the picked master board.
 *
 * UNITS — everything scales off the character:
 *  - a floor's height = `floorFrac` of the world height
 *  - CHAR (character height) = `charRatio` × floor height
 *  - every slot's height  = `r` × CHAR
 *  - `x` is a fraction of world width; the sprite is bottom-center anchored
 *    on its floor's baseline (`wall` slots are lifted onto the wall band).
 *
 * buildStage() renders a slot only when its predicate passes AND its art is
 * loaded (the manifest's graceful-fallback contract). A slot key that never
 * exists in any manifest is a DEV error — caught by the visuals test suite,
 * not silently skipped forever.
 */

export interface VisualSlot {
  /** Sprite manifest key (art-absent = slot silently skipped at runtime). */
  key: string;
  /** Floor index, 0 = ground (bottom of the world). */
  floor: number;
  /** Bottom-center anchor x, fraction of world width (0..1). */
  x: number;
  /** Height in CHAR units (> 0). */
  r: number;
  /** Depth bias within the floor (rope > characters > furniture wall art). */
  z: number;
  /** Wall-mounted: lifted above the baseline by this many CHAR units. */
  wall?: number;
  /** Renders when true for the current state. Omit = always (L0). */
  when?: (s: GameState) => boolean;
}

export interface SceneVisuals {
  /** Floor baselines, fraction of world height measured from the TOP (index 0 = ground). */
  floorBaselines: number[];
  /** One floor's height as a fraction of world height. */
  floorFrac: number;
  /** Character height as a fraction of a floor's height. */
  charRatio: number;
  /** World height in canvas px (>= GAME.height; the pan range). */
  worldHeight: number;
  slots: VisualSlot[];
}

/**
 * The office WORLD (v0.9 pannable stage): three BOARD-DERIVED floor panels
 * (shell bands redrawn at detail; wide ~2:1 rooms where the native 340px
 * characters are ~half a room's height — the board's own proportion),
 * stacked under the pinned hero, with a ROOF band above (billboard gag slot)
 * and the DIRT easter-egg layer below providing the pan depth.
 */
export const PANEL_W = GAME.width; // 1320
/** Per-floor display heights (from the derived panels' real aspects): ground, middle, top. */
export const PANEL_HS = [690, 631, 631] as const;
export const ROOF_TOP = 680; // below hero band (566) + nameplate (64) + gap
export const ROOF_H = 350; // exterior roofline band — the billboard slot
export const PANEL_TOP = ROOF_TOP + ROOF_H; // 1030
export const GROUND_BOTTOM = PANEL_TOP + PANEL_HS[2] + PANEL_HS[1] + PANEL_HS[0]; // 2982
export const DIRT_H = 448; // buried-easter-egg layer under the building
export const OFFICE_WORLD_H = GROUND_BOTTOM + DIRT_H; // 3430
/** Character sprites bake at 340px — the CHAR unit must match them exactly. */
export const CHAR_PX = 340;
/** Standable baseline inset above each panel's bottom edge (floor face). */
export const FLOOR_INSET = 47;

/**
 * OFFICE (master board: office_loaded_a_s99 — scratch/STORYBOARD_office.md).
 * Slot geometry seeded from the scale-table mock (2026-07-17 tuning session);
 * ratios are LIVE TUNING VALUES until Russell freezes them.
 *
 * NOTE: keys are the PLANNED manifest keys for the derived cuts. Until the
 * cut+quantize pass lands them in public/assets/manifest.json, buildStage
 * skips them (placeholder-free stage) — the visuals test tracks the pending
 * set so none can be silently forgotten.
 */
export const OFFICE_VISUALS: SceneVisuals = {
  // Baselines: bottom of each panel minus FLOOR_INSET, as world fractions.
  floorBaselines: [
    (GROUND_BOTTOM - FLOOR_INSET) / OFFICE_WORLD_H, // ground
    (GROUND_BOTTOM - PANEL_HS[0] - FLOOR_INSET) / OFFICE_WORLD_H, // middle
    (GROUND_BOTTOM - PANEL_HS[0] - PANEL_HS[1] - FLOOR_INSET) / OFFICE_WORLD_H, // top
  ],
  floorFrac: 650 / OFFICE_WORLD_H, // nominal floor height
  charRatio: CHAR_PX / 650, // CHAR == the real 340px character sprites
  worldHeight: OFFICE_WORLD_H,
  slots: [
    // ── top floor (2): the owner's office ──────────────────────────────
    { key: "doll-certwall", floor: 2, x: 0.36, r: 0.72, z: 1, wall: 0.95,
      when: (s) => s.account.lifetimeGraduations > 0 },
    { key: "doll-cert-one", floor: 2, x: 0.60, r: 0.30, z: 1, wall: 1.05 },
    // THE OWNER — static fallback only: when the walk sheet is loaded the
    // scene spawns him as an animated actor instead (OfficeScene.spawnOwner).
    { key: "doll-owner", floor: 2, x: 0.30, r: 1.0, z: 2,
      when: () => !isArtLoaded("doll-owner-walk") },
    { key: "doll-desk", floor: 2, x: 0.30, r: 0.60, z: 3 },
    { key: "doll-plant-healthy", floor: 2, x: 0.90, r: 0.72, z: 2 },
    // ── middle floor (1): the waiting room ─────────────────────────────
    // (middle-floor ratios bumped 2026-07-17 — cartoon furniture reads small
    // at real-world proportions; Russell: "second floor stuff looks smaller")
    { key: "doll-binders", floor: 1, x: 0.20, r: 0.52, z: 1, wall: 1.10,
      when: (s) => s.run.complianceOfficerLevel > 0 },
    { key: "doll-portraits", floor: 1, x: 0.48, r: 0.70, z: 1, wall: 0.98 },
    { key: "doll-chair-1", floor: 1, x: 0.15, r: 0.62, z: 2 },
    { key: "doll-chair-2", floor: 1, x: 0.26, r: 0.60, z: 2 },
    { key: "doll-chair-3", floor: 1, x: 0.38, r: 0.66, z: 2 },
    { key: "doll-bucket", floor: 1, x: 0.51, r: 0.38, z: 2 },
    { key: "doll-cooler", floor: 1, x: 0.64, r: 0.95, z: 2 },
    { key: "doll-plant-wilted", floor: 1, x: 0.88, r: 0.80, z: 2 },
    // ── ground floor (0): reception + the lead queue ───────────────────
    { key: "doll-stanchions", floor: 0, x: 0.35, r: 0.48, z: 4 },
    // Intake staffer works the desk — BEHIND the counter (z2 < counter z3).
    { key: "person-staff", floor: 0, x: 0.75, r: 1.0, z: 2 },
    { key: "doll-counter", floor: 0, x: 0.75, r: 0.62, z: 3 },
    { key: "doll-gumball", floor: 0, x: 0.925, r: 0.92, z: 2 },
  ],
};

/** Geometry for one slot in world px (pure — exercised by the test suite). */
export function slotRect(v: SceneVisuals, s: VisualSlot): { x: number; bottom: number; h: number } {
  const floorH = v.floorFrac * v.worldHeight;
  const char = v.charRatio * floorH;
  const baselineY = v.floorBaselines[s.floor];
  if (baselineY === undefined) {
    // Fail loud + field-named: a slot pointing at a floor the scene lacks.
    throw new Error(`slotRect: slot "${s.key}" floor ${s.floor} out of range (scene has ${v.floorBaselines.length})`);
  }
  const h = s.r * char;
  let bottom = baselineY * v.worldHeight;
  if (s.wall !== undefined) bottom -= s.wall * char * 0.55; // lifted onto the wall band
  return { x: s.x * GAME.width, bottom, h };
}

/** All scene tables, keyed like the scene registry. Tiers land per RFC §5 order. */
export const SCENE_VISUALS: Partial<Record<string, SceneVisuals>> = {
  office: OFFICE_VISUALS,
};

/**
 * Keys that are AUTHORED here but whose cut assets haven't shipped yet. The
 * visuals test asserts every slot key is either in the manifest OR in this
 * list — a typo'd key fails loud instead of skipping forever.
 */
export const PENDING_CUT_KEYS: readonly string[] = [
  // (empty — the office cut set shipped 2026-07-17; keys added here only
  // while a slot is authored ahead of its asset)
];

/* ── DETOX (scene 2): med station / deep ward / sub-level ─────────────────
 * Ward B (Russell's depth reference, derived) is the master. The ward's
 * receding floor plane makes depth a staging axis: bed rows march BACK into
 * the hall — higher on canvas = deeper = smaller. 6 ward beds + 3 sub-level
 * beds (the buildBasement permit's) = the sim's max detox capacity (9).
 */
/** Display heights: med compressed to a standard-ish floor; ward/sub natural-ish. */
export const DETOX_PANEL_MED_H = 1000;
export const DETOX_PANEL_WARD_H = 1593; // 928x1120 at full width (natural)
export const DETOX_PANEL_SUB_H = 1300;
export const DETOX_TOP = 680; // below the hero band, office convention
export const DETOX_WARD_TOP = DETOX_TOP + DETOX_PANEL_MED_H; // 1680
export const DETOX_SUB_TOP = DETOX_WARD_TOP + DETOX_PANEL_WARD_H; // 3273
export const DETOX_DIRT_H = 448;
export const DETOX_WORLD_H = DETOX_SUB_TOP + DETOX_PANEL_SUB_H + DETOX_DIRT_H; // 5021
/** Camera park: the ward flush under the pinned hero band. */
export const DETOX_PARK_SCROLL = DETOX_WARD_TOP - 566; // 1114

/** One dollhouse bed slot: world position, per-depth scale (receding rows). */
export interface BedSlot {
  x: number;
  y: number;
  scale: number;
  /** Per-slot texture overrides (e.g. far-arc chairs face the viewer). */
  emptyKey?: string;
  occupiedKey?: string;
  /** Mirror the slot texture horizontally (e.g. the right-end profile chair). */
  flip?: boolean;
}

/**
 * Beds 0..8 — sim bed indexes map straight onto these. Ward rows fill
 * front-to-back (crowding creeps toward the player is the SATIRE, so the
 * FRONT row is beds 0-1); sub-level beds 6-8 exist behind the permit gate.
 * Row geometry eyeballed off ward B (two columns, three receding rows) —
 * LIVE TUNING VALUES.
 */
export const DETOX_BEDS: readonly BedSlot[] = [
  // ward — front row (closest, largest)
  { x: Math.round(0.30 * GAME.width), y: Math.round(DETOX_WARD_TOP + 0.88 * DETOX_PANEL_WARD_H), scale: 1.0 },
  { x: Math.round(0.72 * GAME.width), y: Math.round(DETOX_WARD_TOP + 0.88 * DETOX_PANEL_WARD_H), scale: 1.0 },
  // ward — middle row
  { x: Math.round(0.32 * GAME.width), y: Math.round(DETOX_WARD_TOP + 0.70 * DETOX_PANEL_WARD_H), scale: 0.86 },
  { x: Math.round(0.70 * GAME.width), y: Math.round(DETOX_WARD_TOP + 0.70 * DETOX_PANEL_WARD_H), scale: 0.86 },
  // ward — back row (deepest, smallest)
  { x: Math.round(0.34 * GAME.width), y: Math.round(DETOX_WARD_TOP + 0.55 * DETOX_PANEL_WARD_H), scale: 0.74 },
  { x: Math.round(0.68 * GAME.width), y: Math.round(DETOX_WARD_TOP + 0.55 * DETOX_PANEL_WARD_H), scale: 0.74 },
  // sub-level — the permit's three beds
  { x: Math.round(0.25 * GAME.width), y: Math.round(DETOX_SUB_TOP + 0.85 * DETOX_PANEL_SUB_H), scale: 1.0 },
  { x: Math.round(0.52 * GAME.width), y: Math.round(DETOX_SUB_TOP + 0.85 * DETOX_PANEL_SUB_H), scale: 1.0 },
  { x: Math.round(0.79 * GAME.width), y: Math.round(DETOX_SUB_TOP + 0.85 * DETOX_PANEL_SUB_H), scale: 1.0 },
];

/** The ward's entry door mouth (walk-in spawn) — left end of the ward floor. */
export const DETOX_DOOR = { x: 90, y: DETOX_WARD_TOP + 0.9 * DETOX_PANEL_WARD_H };

/* ── per-tier dollhouse configs (v0.9): TierScene reads these ────────────── */

export interface TierDollhouse {
  /** Floor panels TOP-DOWN: [manifest key, display height in world px]. */
  panels: Array<[string, number]>;
  worldTop: number;
  dirtH: number;
  /** Bed/seat slots in sim bed-index order (world coords + depth scale). */
  slots: readonly BedSlot[];
  slotEmptyKey: string;
  slotOccupiedKey: string;
  /** Walk-in spawn (the floor's entry door mouth). */
  door: { x: number; y: number };
  parkScroll: number;
  staff: { x: number; y: number; tex: string; scale?: number };
  /** Art that must be loaded for the dollhouse path to engage. */
  requiredArt: string[];
}

/* Residential geometry: break room / GROUP circle (main) / cafeteria. */
export const RESI_TOP = 680;
export const RESI_BREAK_H = 1000;
export const RESI_GROUP_H = 1593;
export const RESI_CAFE_H = 802;
export const RESI_GROUP_TOP = RESI_TOP + RESI_BREAK_H; // 1680
export const RESI_CAFE_TOP = RESI_GROUP_TOP + RESI_GROUP_H; // 3273
export const RESI_WORLD_H = RESI_CAFE_TOP + RESI_CAFE_H + 448; // 4523

/** The sharing circle → CLASSROOM staging (Russell 2026-07-17): ALL chairs
 * below with backs to the viewer, facing UP at the facilitator AND the
 * flip-chart behind him — the group sees both. Fill front-first. */
const CIRC_CX = 660;
const ROWS_TOP = RESI_GROUP_TOP; // group panel origin for the fracs below
export const RESI_CHAIRS: readonly BedSlot[] = [
  // front row (closest, largest)
  { x: CIRC_CX - 340, y: ROWS_TOP + Math.round(0.78 * RESI_GROUP_H), scale: 1.0 },
  { x: CIRC_CX + 20, y: ROWS_TOP + Math.round(0.80 * RESI_GROUP_H), scale: 1.0 },
  { x: CIRC_CX + 360, y: ROWS_TOP + Math.round(0.78 * RESI_GROUP_H), scale: 1.0 },
  // middle row
  { x: CIRC_CX - 420, y: ROWS_TOP + Math.round(0.68 * RESI_GROUP_H), scale: 0.9 },
  { x: CIRC_CX - 130, y: ROWS_TOP + Math.round(0.66 * RESI_GROUP_H), scale: 0.9 },
  { x: CIRC_CX + 170, y: ROWS_TOP + Math.round(0.66 * RESI_GROUP_H), scale: 0.9 },
  { x: CIRC_CX + 440, y: ROWS_TOP + Math.round(0.68 * RESI_GROUP_H), scale: 0.9 },
  // back row (deepest)
  { x: CIRC_CX - 250, y: ROWS_TOP + Math.round(0.57 * RESI_GROUP_H), scale: 0.8 },
  { x: CIRC_CX + 290, y: ROWS_TOP + Math.round(0.57 * RESI_GROUP_H), scale: 0.8 },
];
/** Beside the flip-chart (not blocking it) — the class sees him AND the board. */
export const RESI_FACILITATOR = { x: Math.round(0.66 * GAME.width), y: RESI_GROUP_TOP + Math.round(0.50 * RESI_GROUP_H) };

/* ── PHP (scene 4): case management / CLASSROOM (main) / inside the Buggy ──
 * Ruled 2026-07-18: top = benefits-navigation session; main = classroom whose
 * desks ARE the capacity grid (6, facing the back-wall whiteboard, rows
 * recede like the ward); bottom = the Druggy Buggy INTERIOR — the approved
 * frozen tableau (five riders) IS the panel, no slots on it.
 */
export const PHP_TOP = 680;
export const PHP_CASE_H = 754; // aspect-true for the approved tableau panel
export const PHP_CLASS_H = 1593;
export const PHP_BUGGY_H = 1100; // aspect-true for the zoomed tableau crop
export const PHP_CLASS_TOP = PHP_TOP + PHP_CASE_H; // 1434
export const PHP_BUGGY_TOP = PHP_CLASS_TOP + PHP_CLASS_H; // 3027
export const PHP_WORLD_H = PHP_BUGGY_TOP + PHP_BUGGY_H + 448; // 4575

/** 6 desks: two receding rows of three, all facing the back-wall whiteboard. */
export const PHP_DESKS: readonly BedSlot[] = [
  // front row (closest, largest)
  { x: 0.24 * GAME.width, y: PHP_CLASS_TOP + Math.round(0.82 * PHP_CLASS_H), scale: 1.0 },
  { x: 0.5 * GAME.width, y: PHP_CLASS_TOP + Math.round(0.84 * PHP_CLASS_H), scale: 1.0 },
  { x: 0.76 * GAME.width, y: PHP_CLASS_TOP + Math.round(0.82 * PHP_CLASS_H), scale: 1.0 },
  // back row (deeper, smaller)
  { x: 0.27 * GAME.width, y: PHP_CLASS_TOP + Math.round(0.64 * PHP_CLASS_H), scale: 0.85 },
  { x: 0.5 * GAME.width, y: PHP_CLASS_TOP + Math.round(0.66 * PHP_CLASS_H), scale: 0.85 },
  { x: 0.73 * GAME.width, y: PHP_CLASS_TOP + Math.round(0.64 * PHP_CLASS_H), scale: 0.85 },
];


/* ── IOP (scene 5): check-in+testing / GROUP HORSESHOE (main) / the lot ────
 * Ruled 2026-07-19. The group board is Russell's PHOTO of the real room
 * through the style block (worksheet: GROUP BOARD). Facing ruling: far arc
 * of the U = forward-facing chair pair (faces visible), near arc =
 * back-to-viewer pair — per-slot texture overrides, the residential system.
 * Group + lot panels ship UNQUANTIZED (approved-image precedent; the
 * 10-color snap wrecked the photo-derived palette).
 */
export const IOP_TOP = 680;
export const IOP_CHECK_H = 733; // aspect-true (1152x640 board)
export const IOP_GROUP_H = 754; // aspect-true (1344x768 shell)
export const IOP_LOT_H = 880; // aspect-true (1152x768 board)
export const IOP_GROUP_TOP = IOP_TOP + IOP_CHECK_H; // 1413
export const IOP_LOT_TOP = IOP_GROUP_TOP + IOP_GROUP_H; // 2167
export const IOP_WORLD_H = IOP_LOT_TOP + IOP_LOT_H + 448; // 3495

/** 8 chairs along the drawn U — near pair fills first (crowding creeps
 * toward the player); geometry measured off the board. */
export const IOP_CHAIRS: readonly BedSlot[] = [
  // near arc — biggest, backs to viewer
  { x: 0.40 * GAME.width, y: IOP_GROUP_TOP + Math.round(0.86 * IOP_GROUP_H), scale: 1.0,
    emptyKey: "iop-chair-empty-b", occupiedKey: "iop-chair-occupied-b" },
  { x: 0.63 * GAME.width, y: IOP_GROUP_TOP + Math.round(0.84 * IOP_GROUP_H), scale: 1.0,
    emptyKey: "iop-chair-empty-b", occupiedKey: "iop-chair-occupied-b" },
  // side arms — mid depth, faces visible
  { x: 0.315 * GAME.width, y: IOP_GROUP_TOP + Math.round(0.66 * IOP_GROUP_H), scale: 0.78,
    emptyKey: "iop-chair-empty-f", occupiedKey: "iop-chair-occupied-f" },
  { x: 0.735 * GAME.width, y: IOP_GROUP_TOP + Math.round(0.69 * IOP_GROUP_H), scale: 0.78,
    emptyKey: "iop-chair-empty-f", occupiedKey: "iop-chair-occupied-f" },
  // far arc — smallest, facing the viewer
  { x: 0.36 * GAME.width, y: IOP_GROUP_TOP + Math.round(0.545 * IOP_GROUP_H), scale: 0.58,
    emptyKey: "iop-chair-empty-f", occupiedKey: "iop-chair-occupied-f" },
  { x: 0.455 * GAME.width, y: IOP_GROUP_TOP + Math.round(0.525 * IOP_GROUP_H), scale: 0.58,
    emptyKey: "iop-chair-empty-f", occupiedKey: "iop-chair-occupied-f" },
  { x: 0.55 * GAME.width, y: IOP_GROUP_TOP + Math.round(0.525 * IOP_GROUP_H), scale: 0.58,
    emptyKey: "iop-chair-empty-f", occupiedKey: "iop-chair-occupied-f" },
  { x: 0.635 * GAME.width, y: IOP_GROUP_TOP + Math.round(0.55 * IOP_GROUP_H), scale: 0.58,
    emptyKey: "iop-chair-empty-f", occupiedKey: "iop-chair-occupied-f" },
];


/* ── SOBER LIVING (scene 6): shared bedroom / HOUSE MEETING (main) / bath ──
 * Ruled 2026-07-19. Bedroom + bathroom are TABLEAUX (both beds permanently
 * occupied — the offender & the sufferer; the cleaner mid-scrub). The
 * kitchen table seats ARE the capacity grid (6): near pair backs, far pair
 * faces, END pair in side profile (one texture, right end flipped). The
 * TABLE is a prop so far-side sitters draw behind it. Gemini-touched
 * panels ship unquantized (standing rule).
 */
export const SOBER_TOP = 680;
export const SOBER_BED_H = 754; // aspect-true (1344x768)
export const SOBER_KITCH_H = 1593; // deep-floor house convention (squeezed)
export const SOBER_BATH_H = 754; // aspect-true (1344x768)
export const SOBER_KITCH_TOP = SOBER_TOP + SOBER_BED_H; // 1434
export const SOBER_BATH_TOP = SOBER_KITCH_TOP + SOBER_KITCH_H; // 3027
export const SOBER_WORLD_H = SOBER_BATH_TOP + SOBER_BATH_H + 448; // 4229

/** The table the seats orbit (prop; placed by the scene). */
export const SOBER_TABLE = {
  x: 0.5 * GAME.width,
  y: SOBER_KITCH_TOP + Math.round(0.79 * SOBER_KITCH_H),
  // bumped past board-true: cartoon furniture reads small at real-world
  // proportions (the office lesson; Russell: "scale is wrong in the kitchen")
  w: 1050,
  h: 270,
};

/** 6 kitchen seats: near pair fills first (crowding creeps toward the player). */
export const SOBER_SEATS: readonly BedSlot[] = [
  // near side — backs to the viewer, in front of the table
  { x: 0.375 * GAME.width, y: SOBER_KITCH_TOP + Math.round(0.875 * SOBER_KITCH_H), scale: 1.95,
    emptyKey: "sober-chair-empty-b", occupiedKey: "sober-chair-occupied-b" },
  { x: 0.625 * GAME.width, y: SOBER_KITCH_TOP + Math.round(0.875 * SOBER_KITCH_H), scale: 1.95,
    emptyKey: "sober-chair-empty-b", occupiedKey: "sober-chair-occupied-b" },
  // table ends — side profile; the right end is the same texture flipped
  { x: 0.145 * GAME.width, y: SOBER_KITCH_TOP + Math.round(0.79 * SOBER_KITCH_H), scale: 1.8,
    emptyKey: "sober-chair-empty-s", occupiedKey: "sober-chair-occupied-s" },
  { x: 0.855 * GAME.width, y: SOBER_KITCH_TOP + Math.round(0.79 * SOBER_KITCH_H), scale: 1.8,
    emptyKey: "sober-chair-empty-s", occupiedKey: "sober-chair-occupied-s", flip: true },
  // far side — faces visible, behind the table (the table prop occludes)
  { x: 0.41 * GAME.width, y: SOBER_KITCH_TOP + Math.round(0.705 * SOBER_KITCH_H), scale: 1.5,
    emptyKey: "sober-chair-empty-f", occupiedKey: "sober-chair-occupied-f" },
  { x: 0.59 * GAME.width, y: SOBER_KITCH_TOP + Math.round(0.705 * SOBER_KITCH_H), scale: 1.5,
    emptyKey: "sober-chair-empty-f", occupiedKey: "sober-chair-occupied-f" },
];


/* ── ALUMNI (scene 7, the last room): the Grand Hall / the BBQ lawn ────────
 * Ruled 2026-07-20: both BIG, "fitting of making it this far." The warmth is
 * the funnel: the wall is marketing, the chips share a bowl with donations,
 * the referrals and relapses walk the same lawn. Panels unquantized.
 */
export const ALUM_TOP = 680;
export const ALUM_HALL_H = 880; // aspect-true (1152x768)
export const ALUM_BBQ_H = 1593; // deep-lawn house convention
export const ALUM_BBQ_TOP = ALUM_TOP + ALUM_HALL_H; // 1560
export const ALUM_WORLD_H = ALUM_BBQ_TOP + ALUM_BBQ_H + 448; // 3601
export const DOLLHOUSE: Partial<Record<string, TierDollhouse>> = {
  detox: {
    panels: [
      ["detox-floor-med", DETOX_PANEL_MED_H],
      ["detox-floor-ward", DETOX_PANEL_WARD_H],
      ["detox-floor-sub", DETOX_PANEL_SUB_H],
    ],
    worldTop: DETOX_TOP,
    dirtH: DETOX_DIRT_H,
    slots: DETOX_BEDS,
    slotEmptyKey: "detox-cot-empty",
    slotOccupiedKey: "detox-cot-occupied",
    door: DETOX_DOOR,
    parkScroll: DETOX_PARK_SCROLL,
    staff: { x: Math.round(0.62 * GAME.width), y: DETOX_TOP + Math.round(0.88 * DETOX_PANEL_MED_H), tex: "person-nurse" },
    requiredArt: ["detox-floor-med", "detox-floor-ward", "detox-floor-sub", "detox-cot-empty", "detox-cot-occupied"],
  },
  residential: {
    panels: [
      ["resi-floor-break", RESI_BREAK_H],
      ["resi-floor-group", RESI_GROUP_H],
      ["resi-floor-cafe", RESI_CAFE_H],
    ],
    worldTop: RESI_TOP,
    dirtH: 448,
    slots: RESI_CHAIRS,
    slotEmptyKey: "resi-chair-empty",
    slotOccupiedKey: "resi-chair-occupied",
    door: { x: 90, y: RESI_GROUP_TOP + Math.round(0.88 * RESI_GROUP_H) },
    parkScroll: RESI_GROUP_TOP - 566, // group room flush under the hero
    staff: { x: RESI_FACILITATOR.x, y: RESI_FACILITATOR.y, tex: "person-counselor" }, // the facilitator, at the arc focus
    requiredArt: ["resi-floor-break", "resi-floor-group", "resi-floor-cafe", "resi-chair-empty", "resi-chair-occupied"],
  },
  iop: {
    panels: [
      ["iop-floor-check", IOP_CHECK_H],
      ["iop-floor-group", IOP_GROUP_H],
      ["iop-floor-lot", IOP_LOT_H],
    ],
    worldTop: IOP_TOP,
    dirtH: 448,
    slots: IOP_CHAIRS,
    slotEmptyKey: "iop-chair-empty-b",
    slotOccupiedKey: "iop-chair-occupied-b",
    door: { x: 70, y: IOP_GROUP_TOP + Math.round(0.82 * IOP_GROUP_H) },
    parkScroll: IOP_GROUP_TOP - 566, // clamps to max scroll (627) — the horseshoe parks as high as the world allows
    staff: {
      // the CASE MANAGER works the check-in window (top floor)
      x: Math.round(0.33 * GAME.width),
      y: IOP_TOP + Math.round(0.88 * IOP_CHECK_H),
      tex: "person-admin",
    },
    requiredArt: ["iop-floor-check", "iop-floor-group", "iop-floor-lot", "iop-chair-empty-b", "iop-chair-occupied-b", "iop-chair-empty-f", "iop-chair-occupied-f"],
  },
  soberLiving: {
    panels: [
      ["sober-floor-bed", SOBER_BED_H],
      ["sober-floor-kitchen", SOBER_KITCH_H],
      ["sober-floor-bath", SOBER_BATH_H],
    ],
    worldTop: SOBER_TOP,
    dirtH: 448,
    slots: SOBER_SEATS,
    slotEmptyKey: "sober-chair-empty-b",
    slotOccupiedKey: "sober-chair-occupied-b",
    door: { x: 90, y: SOBER_KITCH_TOP + Math.round(0.86 * SOBER_KITCH_H) },
    parkScroll: SOBER_KITCH_TOP - 566,
    staff: {
      // the HOUSE MANAGER presides from beside the fridge, facing the table
      x: Math.round(0.88 * GAME.width),
      y: SOBER_KITCH_TOP + Math.round(0.93 * SOBER_KITCH_H),
      tex: "person-housemgr",
      scale: 1.55,
    },
    requiredArt: ["sober-floor-bed", "sober-floor-kitchen", "sober-floor-bath", "sober-table", "sober-chair-empty-b", "sober-chair-occupied-b", "sober-chair-empty-f", "sober-chair-occupied-f", "sober-chair-empty-s", "sober-chair-occupied-s"],
  },
  php: {
    panels: [
      ["php-floor-case", PHP_CASE_H],
      ["php-floor-class", PHP_CLASS_H],
      ["php-floor-buggy", PHP_BUGGY_H],
    ],
    worldTop: PHP_TOP,
    dirtH: 448,
    slots: PHP_DESKS,
    slotEmptyKey: "php-desk-empty",
    slotOccupiedKey: "php-desk-occupied",
    door: { x: 90, y: PHP_CLASS_TOP + Math.round(0.88 * PHP_CLASS_H) },
    parkScroll: PHP_CLASS_TOP - 566, // classroom flush under the hero
    staff: {
      // the GROUP THERAPIST teaches from beside the whiteboard, facing the class
      x: Math.round(0.63 * GAME.width),
      y: PHP_CLASS_TOP + Math.round(0.58 * PHP_CLASS_H),
      tex: "person-therapist",
    },
    requiredArt: ["php-floor-case", "php-floor-class", "php-floor-buggy", "php-desk-empty", "php-desk-occupied"],
  },
};
